import React, { SetStateAction, Suspense, useCallback, useContext, useEffect, useRef, useState } from "react";
import {
	Button,
	FormControl,
	Grid,
	IconButton,
	InputAdornment,
	MenuItem,
	Paper,
	Select,
	TextField,
	Tooltip
} from "@material-ui/core";
import { withStyles, Theme } from "@material-ui/core/styles";
import { trackPromise } from "react-promise-tracker";
import { Params, useNavigate } from "react-router-dom";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import { KeyboardDatePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";

import SendIcon from "@material-ui/icons/Send";
import InfoIcon from "@material-ui/icons/Info";
import ClearIcon from "@material-ui/icons/Clear";
import EventIcon from "@material-ui/icons/Event";

import { ADMIN_DASHBOARD_HEADER } from "../../../../../constants/caption/dashboardHeader";
import { DynamicForm } from "../../../../../@types/dynamicForm.types";
import SnackbarAPICall from "../../../../shared/snackbar/SnackbarAPICall";
import { SnackbarProps } from "../../../../../@types/snackbarAPI.types";
import { UIContext } from "../../../../../contexts";
import { Validation } from "../../../../../@types/validation.types";
import { api } from "../../../../../utils/api";
import useRouteParam from "../../../../../hooks/useRouteParam";
import { ProvidersDetails, ProvidersInformationMinimized } from "../../../../../@types/components/provider.types";

import "./updateProvidersByTaxId.scss";

const LightTooltip = withStyles((theme: Theme) => ({
	tooltip: {
		backgroundColor: "#f5f5f9",
		color: "rgba(0, 0, 0, 0.87)",
		boxShadow: theme.shadows[1],
		maxWidth: 130,
		fontSize: 11
	}
}))(Tooltip);

const UpdateProvidersByTaxId: React.FC = (): JSX.Element => {
	const [providersInfo, setProvidersInfo] = useState<ProvidersInformationMinimized>();
	const [validation, setValidation] = useState<Validation>({
		providersInfo: {},
		status: "invalid"
	});
	const [editProfileForm, setEditProfileForm] = useState<DynamicForm>();
	const [submitButtonVisibility, setSubmitButtonVisibility] = useState<boolean>(true);
	const [alertResponse, setAlertResponse] = useState(null);
	const [snackbarAPICallProps, setSnackbarAPICallProps] = useState<SnackbarProps>({
		open: false,
		severity: undefined,
		message: "",
		handleSnackbarClose: (event, reason) => {
			if (reason === "clickaway") return;
			setSnackbarAPICallProps(
				Object.assign({}, snackbarAPICallProps, {
					open: false
				})
			);
		}
	});

	const provider_status_options = ["ACTIVE", "TERMINATE"];
	const provider_type_options = ["DIRECT CONTRACT", "BSA"];
	const date = new Date();
	const maxDateRange = date.setDate(date.getDate());

	const { setDashboardHeader } = useContext(UIContext);
	const _validation = useRef<Validation>();
	const navigate = useNavigate();
	const queryGroupNumber = useRouteParam("gn");
	const queryTaxId = useRouteParam("tx");

	const handleValidation = useCallback(async () => {
		if (providersInfo) {
			const { tax_id, provider_status, provider_type } = providersInfo;

			_validation.current = {
				...validation,
				providersInfo: {},
				status: "invalid"
			};
			let flag = true;

			if (tax_id.length === 0) {
				_validation.current.providersInfo["tax_id"] = "Tax-Id is required";
				_validation.current.status = "invalid";
				flag = false;
			}

			if (provider_status.length === 0) {
				_validation.current.providersInfo["provider_status"] = "Provider Status is Required";
				_validation.current.status = "invalid";
				flag = false;
			}

			if (provider_type.length === 0) {
				_validation.current.providersInfo["provider_type"] = "Provider Type is Required";
				_validation.current.status = "invalid";
				flag = false;
			}

			if (flag === true) _validation.current["status"] = "valid";
			else {
				_validation.current["status"] = "invalid";
				alert("Please, fill all required fields!");
			}

			setValidation(Object.assign({}, _validation.current));
			return _validation.current["status"];
		}
	}, [providersInfo, validation]);

	const handleClearDateField = useCallback(
		(event: React.MouseEvent<HTMLButtonElement, MouseEvent>, name: string) => {
			setProvidersInfo(Object.assign({}, providersInfo, { [name]: null }));
		},
		[providersInfo]
	);

	const handleProviderInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = event.target;
		// console.log("Select Field", name, value);

		setProvidersInfo((prevState: SetStateAction<any>) => ({
			...prevState,
			[name]: value
		}));
	}, []);

	const handleDateInputChange = useCallback(
		(_date: MaterialUiPickersDate, name: string) => {
			const date = new Date(_date as Date);
			const parsedDate = date.getFullYear() + "-" + (date.getMonth() + 1) + "-" + date.getDate();
			if (name === "effective_date") setProvidersInfo(Object.assign({}, providersInfo, { [name]: parsedDate }));
			if (name === "termination_date") setProvidersInfo(Object.assign({}, providersInfo, { [name]: parsedDate }));
		},
		[providersInfo]
	);

	const handleGetInitialProvidersData = useCallback(async (queryTaxId: string) => {
		try {
			const filterQuery: Params = {
				tax_id: queryTaxId
			};

			const providersInstanceResponse: ProvidersDetails = await trackPromise(
				api.providers.getProviderDetailsByTaxId(filterQuery)
			);

			if (!providersInstanceResponse) throw new Error();

			const {
				provider_type,
				provider_status,
				tax_id,
				first_name,
				middle_name,
				last_name,
				ZIP,
				address,
				state,
				city,
				contracted_through,
				degree,
				facility_name,
				speciality,
				fee_schedule,
				fax_number,
				effective_date,
				office_name,
				phone_number,
				termination_date
			} = providersInstanceResponse;

			const _provider_type = provider_type === "Direct contract" ? "DIRECT CONTRACT" : provider_type;

			const providersDetailsValue = {
				provider_type: _provider_type,
				provider_status,
				tax_id,
				first_name,
				middle_name,
				last_name,
				ZIP,
				address,
				state,
				city,
				contracted_through,
				degree,
				facility_name,
				speciality,
				fee_schedule,
				fax_number,
				effective_date,
				office_name,
				phone_number,
				termination_date
			};

			// // console.log("RESPONSE_RESPONSE", providersDetailsValue);

			setProvidersInfo(providersDetailsValue);
		} catch (error) {
			alert(error);
		}
	}, []);

	const handleSubmitUpdateProvidersDetails = useCallback(
		async (event: React.MouseEvent<HTMLButtonElement>) => {
			event.preventDefault();
			const validation = await handleValidation();
			// console.log("....", validation);
			try {
				if (validation === "valid" && providersInfo) {
					const {
						provider_type,
						provider_status,
						tax_id,
						first_name,
						middle_name,
						last_name,
						ZIP,
						address,
						state,
						city,
						contracted_through,
						degree,
						facility_name,
						speciality,
						fee_schedule,
						fax_number,
						effective_date,
						office_name,
						phone_number,
						termination_date
					} = providersInfo;

					const updateProvidersPayload = {
						tax_id,
						first_name,
						middle_name,
						last_name,
						provider_status,
						provider_type,
						degree,
						speciality,
						contracted_through,
						facility_name,
						address,
						state,
						city,
						ZIP,
						fee_schedule,
						fax_number,
						effective_date,
						office_name,
						phone_number,
						termination_date
					};

					const response = await api.providers.updateProvidersByTaxID(
						updateProvidersPayload,
						Number(queryGroupNumber)
					);

					// console.log("UPDATE_RESPONSE::---", response);

					if (response) {
						setAlertResponse(response);

						setSnackbarAPICallProps(
							Object.assign({}, snackbarAPICallProps, {
								open: true,
								message: "Data Edited Successfully!",
								severity: "success"
							})
						);

						setTimeout(() => {
							navigate(`/employer/providers?gn=${queryGroupNumber}`);
						}, 2000);
					}
				}
			} catch (error) {
				alert(error);
			}
		},
		[handleValidation, navigate, providersInfo, queryGroupNumber, snackbarAPICallProps]
	);

	useEffect(() => {
		queryTaxId && handleGetInitialProvidersData(String(queryTaxId));
	}, [handleGetInitialProvidersData, queryTaxId]);

	useEffect(() => {
		providersInfo &&
			setEditProfileForm(
				Object.assign(
					{},
					{
						"Provider Updation Form": [
							{
								label: "Tax Id",
								name: "tax_id",
								onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
									handleProviderInputChange(event),
								value: providersInfo.tax_id,
								type: "textfield",
								placeholder: "Enter the Tax-Id"
							},
							{
								label: "Fax Number",
								name: "fax_number",
								onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
									handleProviderInputChange(event),
								value: providersInfo.fax_number,
								type: "textfield",
								placeholder: "Enter the Fax Number"
							},
							{
								label: "First Name",
								name: "first_name",
								onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
									handleProviderInputChange(event),
								value: providersInfo.first_name,
								type: "textfield",
								placeholder: "Enter the first name"
							},
							{
								label: "Middle Name",
								name: "middle_name",
								onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
									handleProviderInputChange(event),
								value: providersInfo.middle_name,
								type: "textfield",
								placeholder: "Enter the middle name"
							},
							{
								label: "Last Name",
								name: "last_name",
								onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
									handleProviderInputChange(event),
								value: providersInfo.last_name,
								type: "textfield",
								placeholder: "Enter the last name"
							},
							{
								label: "Provider Status",
								name: "provider_status",
								onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
									handleProviderInputChange(event),
								value: providersInfo.provider_status,
								type: "selectfield",
								placeholder: "Select the provider status"
							},
							{
								label: "Provider Type",
								name: "provider_type",
								onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
									handleProviderInputChange(event),
								value: providersInfo.provider_type,
								type: "selectfield",
								placeholder: "Select the provider type"
							},
							{
								label: "Degree",
								name: "degree",
								onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
									handleProviderInputChange(event),
								value: providersInfo.degree,
								type: "textfield",
								placeholder: "Select the provider type"
							},
							{
								label: "Speciality",
								name: "speciality",
								onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
									handleProviderInputChange(event),
								value: providersInfo.speciality,
								type: "textfield",
								placeholder: "Select the provider type"
							},
							{
								label: "Contracted Through",
								name: "contracted_through",
								onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
									handleProviderInputChange(event),
								value: providersInfo.contracted_through,
								type: "textfield",
								placeholder: "Select the provider type"
							},
							{
								label: "Facility Name",
								name: "facility_name",
								onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
									handleProviderInputChange(event),
								value: providersInfo.facility_name,
								type: "textfield",
								placeholder: "Select the provider type"
							},
							{
								label: "Fee Schedule",
								name: "fee_schedule",
								onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
									handleProviderInputChange(event),
								value: providersInfo.fee_schedule,
								type: "textfield",
								placeholder: "Select the provider type"
							},
							{
								label: "Effective Date",
								name: "effective_date",
								onChange: (date: MaterialUiPickersDate, name: string) =>
									handleDateInputChange(date, name),
								value: providersInfo.effective_date,
								type: "date",
								placeholder: "Enter the Effective Date"
							},
							{
								label: "Termination Date",
								name: "termination_date",
								onChange: (date: MaterialUiPickersDate, name: string) =>
									handleDateInputChange(date, name),
								value: providersInfo.termination_date,
								type: "date",
								placeholder: "Enter the Termination Date"
							},
							{
								label: "Office Name",
								name: "office_name",
								onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
									handleProviderInputChange(event),
								value: providersInfo.office_name,
								type: "textfield",
								placeholder: "Enter Office Name"
							},
							{
								label: "Address",
								name: "address",
								onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
									handleProviderInputChange(event),
								value: providersInfo.address,
								type: "textfield",
								placeholder: "Select the provider type"
							},
							{
								label: "State",
								name: "state",
								onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
									handleProviderInputChange(event),
								value: providersInfo.state,
								type: "textfield",
								placeholder: "Select the provider type"
							},
							{
								label: "City",
								name: "city",
								onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
									handleProviderInputChange(event),
								value: providersInfo.city,
								type: "textfield",
								placeholder: "Select the provider type"
							},
							{
								label: "Phone Number",
								name: "phone_number",
								onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
									handleProviderInputChange(event),
								value: providersInfo.phone_number,
								type: "textfield",
								placeholder: "Enter the Phone Number"
							},
							{
								label: "ZIP",
								name: "ZIP",
								onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
									handleProviderInputChange(event),
								value: providersInfo.ZIP,
								type: "textfield",
								placeholder: "Select the provider type"
							}
						]
					}
				)
			);
	}, [handleDateInputChange, handleProviderInputChange, providersInfo]);

	useEffect(() => {
		setDashboardHeader(ADMIN_DASHBOARD_HEADER.update_providers_by_tax_id);
	}, [setDashboardHeader]);

	return (
		<div className="update-providers-main-container">
			<Suspense fallback={<div />}>
				<SnackbarAPICall snackbarProps={snackbarAPICallProps} />
			</Suspense>
			<div className="pf-action-button-container" id="pf-action-button-container">
				<Button className="button-green" onClick={handleSubmitUpdateProvidersDetails} variant="contained">
					<span className="button-label-with-icon">Submit</span>
					<span>
						<SendIcon className="button-icon" />
					</span>
				</Button>
			</div>
			<Grid container spacing={1} className="pf-grid-container">
				{editProfileForm &&
					Object.entries(editProfileForm).map(([key, value], index) => {
						// console.log("VALUES_AND_PROPS", value);
						return (
							<div key={index}>
								<div className="pf-title">{key}</div>
								<Paper className="pf-paper-container" variant="outlined" elevation={3}>
									{value.map((field, index) => {
										const { name, label, value, type, onChange, placeholder } = field;
										return (
											<Grid item key={index} xl={12} lg={12} md={12} sm={12} xs={12}>
												<Grid container spacing={1} className="pf-label">
													<Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
														<div className="pf-label-text" id="pf-label-text">
															{label}
														</div>
													</Grid>
													<Grid item xs={9} sm={9} md={9} lg={9} xl={9}>
														{type === "textfield" ? (
															<TextField
																className="input-text-field"
																id={
																	name === "tax_id"
																		? "disabled-input-text-field"
																		: "input-text-field"
																}
																disabled={name === "tax_id" ? true : false}
																value={value}
																name={name}
																onChange={onChange}
																variant="outlined"
																style={{
																	width: "340px",
																	borderRadius: 50
																}}
																placeholder={placeholder}
																helperText={
																	name === "tax_id"
																		? validation.providersInfo.tax_id
																		: name === "provider_status"
																		? validation.providersInfo.provider_status
																		: name === "provider_status"
																		? validation.providersInfo.provider_type
																		: null
																}
																InputProps={{
																	endAdornment: (
																		<>
																			{name === "tax_id" ? (
																				<InputAdornment position="end">
																					<LightTooltip
																						title={
																							"Field can not be edited!"
																						}
																						placement="right-start"
																						arrow
																					>
																						<InfoIcon
																							style={{
																								color: "#808080",
																								fontSize: "20px",
																								// cursor: !showSaveButton
																								// 	? "default"
																								// 	: "pointer",
																								pointerEvents: "unset"
																							}}
																						/>
																					</LightTooltip>
																				</InputAdornment>
																			) : null}
																		</>
																	)
																}}
															/>
														) : type === "selectfield" ? (
															<FormControl>
																<Select
																	variant="outlined"
																	id="text-align-options"
																	name={name}
																	onChange={onChange}
																	style={{
																		minWidth: 180,
																		textAlign: "left",
																		overflowY: "visible"
																	}}
																	placeholder={placeholder}
																	MenuProps={{
																		style: { zIndex: 35960 }
																	}}
																	inputProps={{ "aria-label": "Without label" }}
																	key={index}
																	value={value}
																>
																	<MenuItem value="" disabled>
																		Select Value
																	</MenuItem>
																	{name === "provider_status"
																		? provider_status_options.map(
																				(option, index) => {
																					return (
																						<MenuItem
																							key={index}
																							value={
																								option === "ACTIVE"
																									? "ACTIVE"
																									: option ===
																									  "TERMINATE"
																									? "TERM"
																									: ""
																							}
																						>
																							{option}
																						</MenuItem>
																					);
																				}
																		  )
																		: name === "provider_type"
																		? provider_type_options.map((option, index) => {
																				return (
																					<MenuItem
																						key={index}
																						value={option}
																					>
																						{option}
																					</MenuItem>
																				);
																		  })
																		: null}
																</Select>
																{validation && validation.providersInfo[name] ? (
																	<div className="details">
																		<span className="select-validation-text2">
																			{validation.providersInfo[name]}
																		</span>
																	</div>
																) : null}
															</FormControl>
														) : type === "date" ? (
															<>
																<MuiPickersUtilsProvider utils={DateFnsUtils}>
																	<KeyboardDatePicker
																		className="from-date-input"
																		id="from-date-input"
																		inputVariant="outlined"
																		label={label}
																		placeholder={placeholder}
																		name={name}
																		format="MM/dd/yyyy"
																		error={false}
																		value={value}
																		maxDate={maxDateRange}
																		readOnly={false}
																		onChange={(date: MaterialUiPickersDate) =>
																			onChange(date, name)
																		}
																		InputProps={{
																			startAdornment: (
																				<IconButton
																					onClick={(
																						event: React.MouseEvent<
																							HTMLButtonElement,
																							MouseEvent
																						>
																					) =>
																						handleClearDateField(
																							event,
																							name
																						)
																					}
																					style={{ order: 1 }}
																				>
																					<ClearIcon
																						color="disabled"
																						fontSize="small"
																					/>
																				</IconButton>
																			),
																			readOnly: true
																		}}
																		InputLabelProps={{
																			shrink: true
																		}}
																		//style={{ width: "340px", borderRadius: 50 }}
																		keyboardIcon={
																			<EventIcon
																				style={{
																					color: "#7cb342"
																				}}
																			/>
																		}
																		KeyboardButtonProps={{
																			"aria-label": "change date"
																		}}
																		style={{
																			// cursor: !isButtonSelected ? "not-allowed" : "pointer",
																			pointerEvents: "unset",
																			width: "340px",
																			borderRadius: 50
																		}}
																	/>
																</MuiPickersUtilsProvider>
																{validation.providersInfo[name] ? (
																	<div className="details">
																		<span className="select-validation-text2">
																			{validation.providersInfo[name]}
																		</span>
																	</div>
																) : null}
															</>
														) : null}
													</Grid>
												</Grid>
											</Grid>
										);
									})}
								</Paper>
							</div>
						);
					})}
			</Grid>
		</div>
	);
};

export default UpdateProvidersByTaxId;
